<template>
  <div class="right-navbar" :class="{ collapsed: isCollapsed }">
    <div class="navbar-toggle" @click="toggleNavbar">
      <button v-if="!isCollapsed" class="dashboard-close-btn" title="Collapse">
        <font-awesome-icon size="lg" icon="times" />
      </button>
      <button v-else class="dashboard-close-btn" title="Expand">
        <font-awesome-icon size="lg" icon="bars" />
      </button>
    </div>

    <nav class="navbar-content">
      <!-- Section 1: Main Navigation -->
      <div class="nav-section">
        <div
          v-for="item in section1Items"
          :key="item.id"
          class="nav-item"
          :class="{ active: isSection1ItemActive(item) }"
          @click="handleItemClick(item)"
        >
          <font-awesome-icon :icon="['fas', item.icon]" class="nav-icon" />
          <span v-if="!isCollapsed" class="nav-text">{{ item.text }}</span>
        </div>
      </div>

      <div class="section-divider"></div>

      <!-- Section 2: Project Navigation -->
      <div class="nav-section" v-if="selectedProject">
        <!-- Project Name Header -->
        <div class="nav-section-header">
          <span v-if="!isCollapsed" class="section-title">{{
            selectedProject.name
          }}</span>
        </div>

        <div class="section-header-divider"></div>

        <!-- Retrospective Project Navigation -->
        <template v-if="selectedProject.type === 'retrospective'">
          <!-- Dashboard -->
          <div
            class="nav-item"
            :class="{
              active: isNavItemActive(
                { route: `/${selectedProject.type}/${selectedProjectId}` },
                true
              ),
            }"
            @click="
              handleItemClick({
                id: 'dashboard',
                route: `${selectedProject.type}/${selectedProjectId}`,
              })
            "
          >
            <font-awesome-icon :icon="['fas', 'th']" class="nav-icon" />
            <span v-if="!isCollapsed" class="nav-text">Project Home</span>
          </div>

          <!-- Project Data Section -->
          <div class="nav-group">
            <div class="nav-group-header" :class="{ active: isCollapsed && isGroupActive('projectData') }" @click="toggleGroup('projectData')">
              <div class="nav-group-header-content">
                <font-awesome-icon
                  :icon="['fas', 'database']"
                  class="nav-icon"
                />
                <span v-if="!isCollapsed" class="nav-text"
                  >Project Data</span
                >
              </div>
              <font-awesome-icon
                v-if="!isCollapsed"
                :icon="[
                  'fas',
                  expandedGroups.projectData ? 'chevron-down' : 'chevron-right',
                ]"
                class="expand-icon"
              />
            </div>

            <div
              v-if="expandedGroups.projectData && !isCollapsed"
              class="nav-group-items"
            >
              <div
                class="nav-item nav-subitem"
                :class="{
                  active: isNavItemActive({
                    route: `project/${selectedProjectId}/upload-images`,
                  }),
                }"
                @click="handleItemClick({ id: 'upload-images' })"
              >
                <font-awesome-icon :icon="['fas', 'images']" class="nav-icon" />
                <span class="nav-text">Upload Images</span>
              </div>
              <div
                class="nav-item nav-subitem"
                :class="{
                  active: isNavItemActive({
                    route: `project/${selectedProjectId}/upload-metadata`,
                  }),
                }"
                @click="handleItemClick({ id: 'upload-metadata' })"
              >
                <font-awesome-icon
                  :icon="['fas', 'file-import']"
                  class="nav-icon"
                />
                <span class="nav-text">Upload Metadata</span>
              </div>
              <div
                class="nav-item nav-subitem"
                :class="{
                  active: isNavItemActive({
                    route: `project/${selectedProjectId}/upload-reports`,
                  }),
                }"
                @click="handleItemClick({ id: 'upload-reports' })"
              >
                <font-awesome-icon
                  :icon="['fas', 'file-medical']"
                  class="nav-icon"
                />
                <span class="nav-text">Upload Reports</span>
              </div>
              <div
                class="nav-item nav-subitem"
                :class="{ active: route.path.includes('download-project') }"
                @click="handleItemClick({ id: 'download-project' })"
              >
                <font-awesome-icon
                  :icon="['fas', 'download']"
                  class="nav-icon"
                />
                <span class="nav-text">Download Project</span>
              </div>
            </div>
          </div>

          <!-- Project Configuration Section -->
          <div class="nav-group">
            <div class="nav-group-header" :class="{ active: isCollapsed && isGroupActive('projectConfig') }" @click="toggleGroup('projectConfig')">
              <div class="nav-group-header-content">
                <font-awesome-icon
                  :icon="['fas', 'sliders']"
                  class="nav-icon"
                />
                <span v-if="!isCollapsed" class="nav-text"
                  >Project Settings</span
                >
              </div>
              <font-awesome-icon
                v-if="!isCollapsed"
                :icon="[
                  'fas',
                  expandedGroups.projectConfig
                    ? 'chevron-down'
                    : 'chevron-right',
                ]"
                class="expand-icon"
              />
            </div>

            <div
              v-if="expandedGroups.projectConfig && !isCollapsed"
              class="nav-group-items"
            >
              <div
                class="nav-item nav-subitem"
                :class="{
                  active: isNavItemActive({
                    route: `project/${selectedProjectId}/members`,
                  }),
                }"
                @click="handleItemClick({ id: 'members' })"
              >
                <font-awesome-icon :icon="['fas', 'users']" class="nav-icon" />
                <span class="nav-text">Users</span>
              </div>
              <div
                class="nav-item nav-subitem"
                :class="{
                  active: isNavItemActive({
                    route: `project/${selectedProjectId}/tags`,
                  }),
                }"
                @click="handleItemClick({ id: 'tags' })"
              >
                <font-awesome-icon :icon="['fas', 'tags']" class="nav-icon" />
                <span class="nav-text">Tags</span>
              </div>
              <div
                class="nav-item nav-subitem"
                :class="{
                  active: isNavItemActive({
                    route: `project/${selectedProjectId}/pathologies`,
                  }),
                }"
                @click="handleItemClick({ id: 'pathologies' })"
              >
                <font-awesome-icon :icon="['fas', 'virus']" class="nav-icon" />
                <span class="nav-text">Pathologies</span>
              </div>
              <div
                class="nav-item nav-subitem"
                :class="{
                  active: isNavItemActive({
                    route: `project/${selectedProjectId}/diagnoses`,
                  }),
                }"
                @click="handleItemClick({ id: 'diagnoses' })"
              >
                <font-awesome-icon
                  :icon="['fas', 'stethoscope']"
                  class="nav-icon"
                />
                <span class="nav-text">Diagnoses</span>
              </div>
              <div
                class="nav-item nav-subitem"
                :class="{
                  active: isNavItemActive({
                    route: `project/${selectedProjectId}/subgroups`,
                  }),
                }"
                @click="handleItemClick({ id: 'subgroups' })"
              >
                <font-awesome-icon
                  :icon="['fas', 'layer-group']"
                  class="nav-icon"
                />
                <span class="nav-text">Subgroups</span>
              </div>
              <div
                class="nav-item nav-subitem"
                :class="{
                  active: isNavItemActive({
                    route: `project/${selectedProjectId}/report-extraction`,
                  }),
                }"
                @click="handleItemClick({ id: 'report-extraction' })"
              >
                <font-awesome-icon
                  :icon="['fas', 'file-medical']"
                  class="nav-icon"
                />
                <span class="nav-text">Report Extraction</span>
              </div>
            </div>
          </div>

          <!-- Analytics -->
          <div
            class="nav-item"
            :class="{
              active: isNavItemActive({
                route: `retrospective/${selectedProjectId}/analytics`,
              }),
              disabled: projectBusy,
            }"
            :title="projectBusy ? BUSY_MESSAGE : null"
            @click="handleItemClick({ id: 'analytics' })"
          >
            <font-awesome-icon :icon="['fas', 'chart-line']" class="nav-icon" />
            <span v-if="!isCollapsed" class="nav-text">Analytics</span>
          </div>

          <!-- AI Comparison -->
          <div
            class="nav-item"
            :class="{
              active: isNavItemActive({
                route: `retrospective/${selectedProjectId}/ai-comparison`,
              }),
              disabled: projectBusy,
            }"
            :title="projectBusy ? BUSY_MESSAGE : null"
            @click="handleItemClick({ id: 'ai-comparison' })"
          >
            <font-awesome-icon :icon="['fas', 'code-compare']" class="nav-icon" />
            <span v-if="!isCollapsed" class="nav-text">AI Comparison</span>
          </div>
        </template>

        <!-- Prospective Project Navigation -->
        <template v-if="selectedProject.type === 'prospective'">
          <!-- Dashboard -->
          <div
            class="nav-item"
            :class="{
              active: isNavItemActive(
                { route: `/${selectedProject.type}/${selectedProjectId}` },
                true
              ),
            }"
            @click="
              handleItemClick({
                id: 'dashboard',
                route: `${selectedProject.type}/${selectedProjectId}`,
              })
            "
          >
            <font-awesome-icon :icon="['fas', 'th']" class="nav-icon" />
            <span v-if="!isCollapsed" class="nav-text">Dashboard</span>
          </div>

          <!-- Project Configuration Section -->
          <div class="nav-group">
            <div class="nav-group-header" :class="{ active: isCollapsed && isGroupActive('projectConfig') }" @click="toggleGroup('projectConfig')">
              <div class="nav-group-header-content">
                <font-awesome-icon
                  :icon="['fas', 'sliders']"
                  class="nav-icon"
                />
                <span v-if="!isCollapsed" class="nav-text"
                  >Project Settings</span
                >
              </div>
              <font-awesome-icon
                v-if="!isCollapsed"
                :icon="[
                  'fas',
                  expandedGroups.projectConfig
                    ? 'chevron-down'
                    : 'chevron-right',
                ]"
                class="expand-icon"
              />
            </div>

            <div
              v-if="expandedGroups.projectConfig && !isCollapsed"
              class="nav-group-items"
            >
              <div
                class="nav-item nav-subitem"
                :class="{
                  active: isNavItemActive({
                    route: `project/${selectedProjectId}/members`,
                  }),
                }"
                @click="handleItemClick({ id: 'members' })"
              >
                <font-awesome-icon :icon="['fas', 'users']" class="nav-icon" />
                <span class="nav-text">Users</span>
              </div>
              <div
                class="nav-item nav-subitem"
                :class="{
                  active: isNavItemActive({
                    route: `project/${selectedProjectId}/tags`,
                  }),
                }"
                @click="handleItemClick({ id: 'tags' })"
              >
                <font-awesome-icon :icon="['fas', 'tags']" class="nav-icon" />
                <span class="nav-text">Tags</span>
              </div>
              <div
                class="nav-item nav-subitem"
                :class="{
                  active: isNavItemActive({
                    route: `project/${selectedProjectId}/pathologies`,
                  }),
                }"
                @click="handleItemClick({ id: 'pathologies' })"
              >
                <font-awesome-icon :icon="['fas', 'virus']" class="nav-icon" />
                <span class="nav-text">Pathologies</span>
              </div>
              <div
                class="nav-item nav-subitem"
                :class="{
                  active: isNavItemActive({
                    route: `project/${selectedProjectId}/diagnoses`,
                  }),
                }"
                @click="handleItemClick({ id: 'diagnoses' })"
              >
                <font-awesome-icon
                  :icon="['fas', 'stethoscope']"
                  class="nav-icon"
                />
                <span class="nav-text">Diagnoses</span>
              </div>
              <div
                class="nav-item nav-subitem"
                :class="{
                  active: isNavItemActive({
                    route: `project/${selectedProjectId}/subgroups`,
                  }),
                }"
                @click="handleItemClick({ id: 'subgroups' })"
              >
                <font-awesome-icon
                  :icon="['fas', 'layer-group']"
                  class="nav-icon"
                />
                <span class="nav-text">Subgroups</span>
              </div>
            </div>
          </div>

          <!-- AI Evaluation -->
          <div
            class="nav-item"
            :class="{
              active: isNavItemActive({
                route: `${selectedProject.type}/${selectedProjectId}/ai-evaluation`,
              }),
            }"
            @click="handleItemClick({ id: 'ai-evaluation' })"
          >
            <font-awesome-icon :icon="['fas', 'robot']" class="nav-icon" />
            <span v-if="!isCollapsed" class="nav-text">AI Evaluation</span>
          </div>

          <!-- Reader Evaluation -->
          <div
            class="nav-item"
            :class="{
              active: isNavItemActive({
                route: `${selectedProject.type}/${selectedProjectId}/reader-evaluation`,
              }),
            }"
            @click="handleItemClick({ id: 'reader-evaluation' })"
          >
            <font-awesome-icon :icon="['fas', 'user-doctor']" class="nav-icon" />
            <span v-if="!isCollapsed" class="nav-text">Reader Evaluation</span>
          </div>

          <!-- Technical Evaluation -->
          <div
            class="nav-item"
            :class="{
              active: isNavItemActive({
                route: `${selectedProject.type}/${selectedProjectId}/technical-evaluation`,
              }),
            }"
            @click="handleItemClick({ id: 'technical-evaluation' })"
          >
            <font-awesome-icon :icon="['fas', 'microchip']" class="nav-icon" />
            <span v-if="!isCollapsed" class="nav-text">Technical Evaluation</span>
          </div>
        </template>

        <!-- Prospective Light Project Navigation -->
        <template v-if="selectedProject.type === 'pros-light'">
          <!-- Dashboard -->
          <div
            class="nav-item"
            :class="{
              active: isNavItemActive({
                route: `${selectedProject.type}/${selectedProjectId}`,
              }),
            }"
            @click="
              handleItemClick({
                id: 'dashboard',
                route: `${selectedProject.type}/${selectedProjectId}`,
              })
            "
          >
            <font-awesome-icon :icon="['fas', 'th']" class="nav-icon" />
            <span v-if="!isCollapsed" class="nav-text">Project Home</span>
          </div>

          <!-- Project Configuration Section -->
          <div class="nav-group">
            <div class="nav-group-header" :class="{ active: isCollapsed && isGroupActive('projectConfig') }" @click="toggleGroup('projectConfig')">
              <div class="nav-group-header-content">
                <font-awesome-icon
                  :icon="['fas', 'sliders']"
                  class="nav-icon"
                />
                <span v-if="!isCollapsed" class="nav-text"
                  >Project Settings</span
                >
              </div>
              <font-awesome-icon
                v-if="!isCollapsed"
                :icon="[
                  'fas',
                  expandedGroups.projectConfig
                    ? 'chevron-down'
                    : 'chevron-right',
                ]"
                class="expand-icon"
              />
            </div>

            <div
              v-if="expandedGroups.projectConfig && !isCollapsed"
              class="nav-group-items"
            >
              <div
                class="nav-item nav-subitem"
                @click="handleItemClick({ id: 'members' })"
              >
                <font-awesome-icon :icon="['fas', 'users']" class="nav-icon" />
                <span class="nav-text">Users</span>
              </div>

              <div
                class="nav-item nav-subitem"
                @click="handleItemClick({ id: 'subgroups' })"
              >
                <font-awesome-icon
                  :icon="['fas', 'layer-group']"
                  class="nav-icon"
                />
                <span class="nav-text">Subgroups</span>
              </div>
            </div>
          </div>

          <!-- AI Performance Analytics Section -->
          <div
            class="nav-item"
            :class="{
              active: isNavItemActive({
                route: `${selectedProject.type}/${selectedProjectId}/ai-performance-analytics`,
              }),
            }"
            @click="handleItemClick({ id: 'ai-performance-analytics' })"
          >
            <font-awesome-icon :icon="['fas', 'chart-line']" class="nav-icon" />
            <span v-if="!isCollapsed" class="nav-text">AI Performance Analytics</span>
          </div>
        </template>
      </div>
    </nav>

    <div class="section-divider"></div>

    <!-- Section 3: Account Actions (Bottom) -->
    <div class="navbar-bottom">
      <div
        v-for="item in section3Items"
        :key="item.id"
        class="nav-item"
        @click="handleItemClick(item)"
      >
        <font-awesome-icon :icon="['fas', item.icon]" class="nav-icon" />
        <span v-if="!isCollapsed" class="nav-text">{{ item.text }}</span>
      </div>
    </div>

    <!-- Unsaved Changes Warning Modal -->
    <BModal
      v-model="showUnsavedWarning"
      title="Unsaved Changes"
      header-class="modal-header-custom"
      title-class="text-white h4"
      ok-title="Leave"
      cancel-title="Stay"
      @ok="confirmLeave"
    >
      <p>You have unsaved annotations. Are you sure you want to leave?</p>
    </BModal>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";

const store = useStore();
const router = useRouter();
const route = useRoute();

const isCollapsed = computed(
  () => store.state.workspacePanel.rightPanelCollapsed
);

// Labeler state for unsaved changes warning
const isLabelerActive = computed(() => store.state.workspacePanel.labelerMode);
const hasUnsavedChanges = computed(
  () => store.state.labelerAnnotation?.hasUnsavedChanges || false
);
const showUnsavedWarning = ref(false);
const pendingNavigation = ref(null);

// Manage expanded/collapsed state for collapsible groups
const expandedGroups = ref({
  projectData: false,
  projectConfig: false,
});

const toggleGroup = (groupName) => {
  // When collapsed, navigate to first child and expand the group
  if (isCollapsed.value) {
    const firstChildMap = {
      projectData: "upload-images",
      projectConfig: "members",
    };

    const firstChildId = firstChildMap[groupName];
    if (firstChildId) {
      handleItemClick({ id: firstChildId });
    }

    // Expand the group if not already expanded
    if (!expandedGroups.value[groupName]) {
      expandedGroups.value[groupName] = true;
    }
  } else {
    // Normal toggle behavior when expanded
    expandedGroups.value[groupName] = !expandedGroups.value[groupName];
  }
};

// Get selected project
const selectedProjectId = computed(
  () => store.state.projectSubmission.selectedProjectId
);
const projects = computed(() => store.state.projectSubmission.projects || []);
const selectedProject = computed(() =>
  projects.value.find((p) => p._id === selectedProjectId.value)
);

// Metrics recalculation in progress - the dashboards would show stale or
// partial numbers, so they stay locked until the 30s project poll clears it
const projectBusy = computed(() => selectedProject.value?.busy === true);
const BUSY_MESSAGE =
  "Project metrics are being recalculated. Dashboards will be available once it completes.";
const BUSY_BLOCKED_ITEMS = ["analytics", "ai-comparison"];
const BUSY_BLOCKED_ROUTES = ["Analytics", "AIComparison"];

// Covers the two cases a click guard can't: a dashboard deep-linked/reloaded
// while busy, and a recalculation that starts with a dashboard already open
watch(
  [projectBusy, () => route.name],
  ([busy, routeName]) => {
    if (busy && BUSY_BLOCKED_ROUTES.includes(routeName)) {
      store.dispatch("alert/warning", BUSY_MESSAGE);
      router.push(`/retrospective/${route.params.projectId}`);
    }
  },
  { immediate: true },
);

// Get project type icon
const projectTypeIcon = computed(() => {
  if (!selectedProject.value) return "folder";
  if (selectedProject.value.type === "retrospective")
    return "clock-rotate-left";
  if (selectedProject.value.type === "pros-light") return "gauge-simple";
  return "chart-line";
});

// Section 1: Main Navigation
const section1Items = [
  { id: "home", icon: "home", text: "Home" },
  { id: "manual", icon: "book", text: "User Manual" },
];

// Section 3: Account Actions
const section3Items = [
  { id: "support", icon: "life-ring", text: "Support" },
  { id: "change-password", icon: "lock", text: "Change Password" },
  { id: "logout", icon: "right-from-bracket", text: "Logout" },
];

// Check if section 1 item is active
const isSection1ItemActive = (item) => {
  if (item.id === "home") {
    // Home is active when on workspace root without a selected project
    return route.name === "Workspace" && !selectedProjectId.value;
  }
  return false;
};

// Check if nav item is active
const isNavItemActive = (item, equals = false) => {
  if (item.route) {
    if (equals) {
      return route.path === item.route;
    }
    return route.path.includes(item.route);
  }
  return false;
};

// Check if any child in a nav group is active (for collapsed state highlighting)
const isGroupActive = (groupName) => {
  const groupRoutes = {
    projectData: ["upload-images", "upload-metadata", "upload-reports", "download-project"],
    projectConfig: ["members", "tags", "pathologies", "diagnoses", "subgroups", "report-extraction"],
  };

  const routes = groupRoutes[groupName];
  if (!routes) return false;

  return routes.some((r) => route.path.includes(r));
};

const toggleNavbar = () => {
  store.dispatch("workspacePanel/toggleRightPanel");
};

// Check if labeler has unsaved changes and show warning modal if needed
const handleItemClick = (item) => {
  if (projectBusy.value && BUSY_BLOCKED_ITEMS.includes(item.id)) {
    store.dispatch("alert/warning", BUSY_MESSAGE);
    return;
  }

  // Check if labeler is active and has unsaved changes
  if (isLabelerActive.value && hasUnsavedChanges.value) {
    // Store the pending navigation and show warning modal
    pendingNavigation.value = () => executeNavigation(item);
    showUnsavedWarning.value = true;
    return;
  }

  // No unsaved changes, proceed with navigation
  executeNavigation(item);
};

// Confirm leaving labeler with unsaved changes
const confirmLeave = () => {
  if (pendingNavigation.value) {
    // Exit labeler mode before navigating
    store.dispatch("workspacePanel/exitLabelerMode");
    pendingNavigation.value();
    pendingNavigation.value = null;
  }
};

// Execute the actual navigation
const executeNavigation = (item) => {
  console.log("Clicked:", item.id);
  store.dispatch("workspacePanel/clearSelectedImage");

  // Exit labeler mode if active (for cases without unsaved changes)
  if (isLabelerActive.value) {
    store.dispatch("workspacePanel/exitLabelerMode");
  }

  if (item.id === "home") {
    // Clear selected project and navigate to workspace home
    store.dispatch("projectSubmission/selectProject", null);
    router.push({ name: "Workspace" });
  } else if (item.id === "manual") {
    // Open user manual in a new tab
    window.open("/manuals/RATIFY%20Orchestrator%201.5.0.1%20New.pdf", "_blank");
  } else if (item.id === "add-project") {
    // Open create project modal
    store.dispatch("workspacePanel/setShowCreateProjectModal", true);
  } else if (item.id === "license") {
    // Open license allocation modal
    store.dispatch("workspacePanel/setShowLicenseModal", true);
  } else if (item.id === "support") {
    // Open support tab in the new windows
    window.open("https://www.envisionit.ai/support/", "_blank");
  } else if (item.id === "change-password") {
    // Open change password modal
    store.dispatch("workspacePanel/setShowChangePasswordModal", true);
  } else if (item.id === "logout") {
    // Logout
    store.dispatch("auth/logout");
  } else if (item.id === "analytics") {
    store.dispatch("workspacePanel/setLeftPanelCollapsed", true);
    router.push(`/retrospective/${selectedProjectId.value}/analytics`);
  } else if (item.id === "download-project") {
    // Navigate to Download Project
    router.push(`/retrospective/${selectedProjectId.value}/download-project`);
  } else if (item.id === "ai-evaluation") {
    store.dispatch("workspacePanel/setLeftPanelCollapsed", true);
    router.push(`/prospective/${selectedProjectId.value}/ai-evaluation`);
  } else if (item.id === "reader-evaluation") {
    store.dispatch("workspacePanel/setLeftPanelCollapsed", true);
    router.push(`/prospective/${selectedProjectId.value}/reader-evaluation`);
  } else if (item.id === "technical-evaluation") {
    store.dispatch("workspacePanel/setLeftPanelCollapsed", true);
    router.push(`/prospective/${selectedProjectId.value}/technical-evaluation`);
  } else if (item.id === "ai-performance-analytics") {
    store.dispatch("workspacePanel/setLeftPanelCollapsed", true);
    router.push(`/pros-light/${selectedProjectId.value}/ai-performance-analytics`);
  } else if (item.id === "members") {
    // Navigate to Members Management
    router.push(`/project/${selectedProjectId.value}/members`);
  } else if (item.id === "tags") {
    // Navigate to Tags Management
    router.push(`/project/${selectedProjectId.value}/tags`);
  } else if (item.id === "pathologies") {
    // Navigate to Pathologies Management
    router.push(`/project/${selectedProjectId.value}/pathologies`);
  } else if (item.id === "diagnoses") {
    // Navigate to Diagnoses Management
    router.push(`/project/${selectedProjectId.value}/diagnoses`);
  } else if (item.id === "subgroups") {
    // Navigate to Subgroups Management
    router.push(`/project/${selectedProjectId.value}/subgroups`);
  } else if (item.id === "report-extraction") {
    // Navigate to Report Extraction configuration
    router.push(`/project/${selectedProjectId.value}/report-extraction`);
  } else if (item.id === "upload-images") {
    // Navigate to Upload Images
    router.push(`/project/${selectedProjectId.value}/upload-images`);
  } else if (item.id === "upload-metadata") {
    // Navigate to Upload Metadata
    router.push(`/project/${selectedProjectId.value}/upload-metadata`);
  } else if (item.id === "upload-reports") {
    // Navigate to Upload Reports
    router.push(`/project/${selectedProjectId.value}/upload-reports`);
  } else if (item.id === "ai-comparison") {
    store.dispatch("workspacePanel/setLeftPanelCollapsed", true);
    router.push(`/retrospective/${selectedProjectId.value}/ai-comparison`);
  } else if (item.route) {
    // Navigate to the specific route
    router.push(`/${item.route}`);
  }
};
</script>

<style scoped>
.right-navbar {
  position: fixed;
  top: var(--header-height);
  left: 0;
  height: calc(100vh - var(--header-height));
  background-color: var(--color-bg-secondary);
  border-right: 1px solid #1e4f78;
  box-shadow: 2px 0 8px rgba(30, 79, 120, 0.3);
  transition: all 0.3s ease;
  width: var(--panel-right-width);
  z-index: 1000;
  display: flex;
  flex-direction: column;
}

.right-navbar.collapsed {
  width: var(--panel-collapsed-width);
}

.navbar-toggle {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 1rem 1.5rem;
  cursor: pointer;
  color: var(--color-text-primary);
  border-bottom: 3px solid var(--color-divider-navy);
}

.collapsed .navbar-toggle {
  justify-content: center;
}

.navbar-content {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
}

.nav-section {
  display: flex;
  flex-direction: column;
}

.section-divider {
  height: 1px;
  background-color: var(--color-divider-navy);
  margin: 0.5rem 0;
}

.navbar-bottom {
  padding: 0.5rem 0 1rem;
  background-color: var(--color-bg-secondary);
}

.nav-section-header {
  display: flex;
  align-items: center;
  padding: 0.5rem 1.5rem;
  gap: 0.5rem;
}

.section-icon {
  color: var(--color-accent-secondary);
  font-size: 0.85rem;
  min-width: 0.85rem;
}

.section-title {
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-semibold);
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.collapsed .nav-section-header {
  padding: 0.5rem 1rem;
}

.section-header-divider {
  height: 1px;
  background-color: var(--color-divider-navy);
  margin: 0 1rem 0.5rem 1rem;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  cursor: pointer;
  color: var(--color-text-primary);
  transition: all 0.2s ease;
  white-space: nowrap;
  gap: 0.75rem;
  border-left: var(--border-width-card) solid transparent;
}

.nav-item:hover {
  background-color: var(--color-bg-secondary);
  border-left-color: var(--color-border-primary);
}

.nav-item.active {
  background-color: var(--color-bg-secondary);
  border-left-color: var(--color-accent-primary);
  font-weight: var(--font-weight-bold);
}

.nav-item.active .nav-icon {
  color: var(--color-accent-primary);
}

.nav-item.disabled,
.nav-item.disabled .nav-icon {
  opacity: 0.45;
  cursor: not-allowed;
}

.nav-item.disabled:hover {
  background-color: transparent;
  border-left-color: transparent;
}

.nav-icon {
  font-size: 1.1rem;
  min-width: 1.1rem;
  transition: all 0.3s ease;
  color: var(--color-accent-primary);
}

.collapsed .nav-icon {
  font-size: 1rem;
}

.nav-text {
  font-size: 0.85rem;
  opacity: 1;
  transition: opacity 0.2s ease;
  font-weight: var(--font-weight-normal);
}

.collapsed .nav-item {
  padding: 0.5rem 1rem;
}

/* Custom scrollbar */
.navbar-content::-webkit-scrollbar {
  width: var(--scrollbar-width);
}

.navbar-content::-webkit-scrollbar-track {
  background: var(--color-scrollbar-track);
}

.navbar-content::-webkit-scrollbar-thumb {
  background: var(--color-scrollbar-thumb);
  border-radius: var(--border-radius-small);
}

.navbar-content::-webkit-scrollbar-thumb:hover {
  background: var(--color-scrollbar-thumb-hover);
}

/* Collapsible group styles */
.nav-group {
  display: flex;
  flex-direction: column;
}

.nav-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  cursor: pointer;
  color: var(--color-text-primary);
  transition: all 0.2s ease;
  border-left: var(--border-width-card) solid transparent;
}

.nav-group-header:hover {
  background-color: var(--color-bg-secondary);
  border-left-color: var(--color-border-primary);
}

.nav-group-header.active {
  background-color: var(--color-bg-secondary);
  border-left-color: var(--color-accent-primary);
}

.nav-group-header.active .nav-icon {
  color: var(--color-accent-primary);
}

.nav-group-header-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.expand-icon {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  transition: transform 0.2s ease;
}

.nav-group-items {
  display: flex;
  flex-direction: column;
}

.nav-subitem {
  padding-left: 2.5rem;
  font-size: 0.8rem;
}

.nav-subitem .nav-icon {
  font-size: 0.9rem;
}

.collapsed .nav-group-header {
  padding: 0.5rem 1rem;
}

/* Modal styles */
:deep(.modal-content) {
  background-color: var(--color-bg-secondary);
  color: var(--color-text-primary);
}

:deep(.modal-header) {
  background-color: var(--color-bg-primary);
  border-bottom: var(--border-width-standard) solid var(--color-border-primary);
}

:deep(.modal-title) {
  color: var(--color-text-primary);
  font-weight: var(--font-weight-extrabold);
}

:deep(.modal-body) {
  color: var(--color-text-primary);
}

:deep(.btn-primary) {
  background-color: var(--color-accent-primary);
  border-color: var(--color-accent-primary);
  color: var(--color-bg-primary);
  font-weight: var(--font-weight-extrabold);
}

:deep(.btn-secondary) {
  background-color: var(--color-button-secondary);
  border-color: var(--color-button-secondary);
  color: var(--color-text-primary);
  font-weight: var(--font-weight-extrabold);
}
</style>
